import { Component, HostListener, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Subscription, interval } from 'rxjs';
import moment from 'moment-timezone';
import {
  AlertNotificationService,
  ApiService,
  CommonFunctionsService,
  DatastoreService,
  ExtendStateService,
  RescheduleService,
  lineOutstanding,
  lineTotal,
  round2,
} from '../../services';

@Component({
  selector: 'app-payment-verify',
  templateUrl: './payment-verify.component.html',
  styleUrls: ['./payment-verify.component.scss']
})
export class PaymentVerifyComponent implements OnInit, OnDestroy {

  bookingId: any;
  purchaseId: any;
  companyId: any;

  booking: any;
  purchase: any;
  member: any;
  company: any;
  parent: any;
  service: any;

  readonly maxRetries = 100;
  private readonly intervalMs = 10000;
  pollCount = 0;
  private pollSub: Subscription | null = null;
  private resolved = false;
  private paymentStarted: moment.Moment | null = null;
  private verifying = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiService: ApiService,
    private dataStore: DatastoreService,
    private commonFunctions: CommonFunctionsService,
    private rescheduleService: RescheduleService,
    private extendState: ExtendStateService,
    private alerts: AlertNotificationService,
  ) { }

  ngOnInit(): void {
    const authToken = localStorage.getItem('auth_token');
    if (authToken) {
      this.dataStore.authToken = authToken;
    }
    this.verify();
  }

  ngOnDestroy(): void {
    this.pollSub?.unsubscribe();
  }

  // Browsers show their own generic leave-site message; the custom text here
  // is ignored but still required to trigger the prompt.
  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.verifying) {
      event.preventDefault();
      event.returnValue =
        'Navigating away while verifying payment may cause issues with your booking';
    }
  }

  async verify() {
    // HeyCentric's "Cancel" button returns to the verify URL with Rc=C
    // appended (plus Pmt/SID/hash) — no payment exists, so don't poll.
    if (this.route.snapshot.queryParamMap.get('Rc') === 'C') {
      this.clearPendingState();
      this.router.navigate([
        `${this.commonFunctions.getCompanyName()}/${this.commonFunctions.getCompanyId()}/our-ceremony`,
      ]);
      return;
    }

    this.verifying = true;

    try {
      if (this.route.snapshot.queryParamMap.has('bpc')) {
        const bpcParts = this.route.snapshot.queryParamMap.get('bpc')?.split('|') || [];
        this.bookingId = bpcParts[0];
        this.purchaseId = bpcParts[1];
        this.companyId = bpcParts[2];
      } else {
        this.bookingId = this.route.snapshot.queryParamMap.get('b');
        this.purchaseId = this.route.snapshot.queryParamMap.get('p');
        this.companyId = this.route.snapshot.queryParamMap.get('c');
      }
    } catch (err) {
      console.error(err);
    }

    if (!this.bookingId || !this.purchaseId || !this.companyId) {
      console.error('No information returned from payment provider to verify purchase');
      this.commonFunctions.setRequestError({ message: 'Required fields not found' });
      await this.redirect('failed');
      return;
    }

    try {
      this.company = await this.apiService.getCompany(this.companyId);
      this.parent = await this.apiService.getCompany(this.company.parent);
      this.booking = await this.apiService.getBooking(this.company.id, this.bookingId);
      this.service = await this.apiService.getService(this.company.id, this.booking.service_id);
      this.purchase = await this.apiService.getPurchase(this.company.id, this.service.id, this.purchaseId);
      this.member = await this.apiService.getMemberDetails(this.company.id, this.booking.client_id);
    } catch (err) {
      console.error(err);
      this.commonFunctions.setRequestError(err);
      await this.redirect('failed');
      return;
    }

    if (!(this.company && this.parent && this.booking && this.service && this.purchase && this.member)) {
      await this.redirect('failed');
      return;
    }

    this.dataStore.selectedCompany = this.company;
    this.dataStore.selectedService = this.service;
    this.dataStore.rootCompany = this.parent;
    this.dataStore.company = this.company;
    this.dataStore.booking = this.booking;

    let paymentStartedRaw = this.booking?.settings?.payment_started;
    if (!paymentStartedRaw) {
      // The stamp write can fail on the summary page (it is stamped right
      // before the gateway redirect); every stamping flow also writes the
      // stamped booking to localStorage['ceremony'], so fall back to that
      // copy rather than reading a lost stamp as a failed payment. Live
      // incident: booking 11271, 20 Aug 2026 — the stamp never landed on
      // JRNI, this branch redirected 'failed' and clearPendingState()
      // destroyed the paid-for add-on links.
      try {
        const cached = JSON.parse(localStorage.getItem('ceremony') ?? 'null');
        if (cached && String(cached.id) === String(this.booking?.id)) {
          paymentStartedRaw = cached?.settings?.payment_started;
          if (paymentStartedRaw) {
            console.warn(
              'payment_started missing from the fetched booking — using the localStorage copy',
              paymentStartedRaw,
            );
          }
        }
      } catch {
        // unparseable cache — fall through to the unresolvable-window path
      }
    }
    if (!paymentStartedRaw) {
      // No window anywhere. The customer may still have paid — a missing
      // stamp says nothing about the gateway outcome — so the pending
      // follow-up state must survive for support to replay; only a genuine
      // "no payment arrived" outcome may clear it.
      console.error('booking.settings.payment_started not set — cannot determine payment window');
      await this.redirect('failed', { preservePendingState: true });
      return;
    }
    this.paymentStarted = moment.utc(paymentStartedRaw);
    console.log('payment_started', this.paymentStarted.toISOString());

    // The page shows its own always-on overlay spinner (see template), so there
    // is no global ngx-spinner to juggle here. The polling call is sent with
    // no-loader (see ApiService.getPurchasePayments) so it never toggles the
    // global spinner either.

    // Perform one immediate check before starting the RxJS loop
    const foundImmediately = await this.checkForNewPayment();
    if (foundImmediately) {
      await this.redirect('success');
      return;
    }

    this.startPolling();
  }

  private startPolling() {
    this.pollSub = interval(this.intervalMs).subscribe(async () => {
      if (this.resolved) {
        return;
      }
      this.pollCount += 1;
      console.log(`payment-verify poll ${this.pollCount}/${this.maxRetries}`);
      const found = await this.checkForNewPayment();
      if (found) {
        this.resolved = true;
        this.pollSub?.unsubscribe();
        await this.redirect('success');
        return;
      }
      if (this.pollCount >= this.maxRetries) {
        this.resolved = true;
        this.pollSub?.unsubscribe();
        console.error(`payment-verify retry limit of ${this.maxRetries} reached`);
        await this.redirect('failed');
      }
    });
  }

  private async checkForNewPayment(): Promise<boolean> {
    try {
      const payments: any = await this.apiService.getPurchasePayments(
        this.company.id,
        this.service.id,
        this.purchase.id
      );
      const list: any[] = payments?._embedded?.payments || [];
      for (const p of list) {
        const createdAt = p.created_at ? moment.utc(p.created_at) : null;
        if (createdAt && this.paymentStarted && createdAt.isAfter(this.paymentStarted)) {
          console.log('found new payment after payment_started', p);
          return true;
        }
      }
    } catch (err) {
      console.error('Error polling getPurchasePayments', err);
    }
    return false;
  }

  // The warning stays active through the post-payment follow-up writes in
  // redirect() — the in-app navigation destroys the component, which removes
  // the window listener.
  private async redirect(
    result: 'success' | 'failed',
    opts: { preservePendingState?: boolean } = {},
  ): Promise<void> {
    const base = `${this.commonFunctions.getCompanyName()}/${this.commonFunctions.getCompanyId()}`;
    const action = localStorage.getItem('paymentAction');

    if (result === 'failed') {
      // Clear pending state so a future retry starts clean — the poll ran its
      // course and the webhook never recorded a payment, so there is nothing
      // to follow up. preservePendingState is for the one failed path where
      // that is NOT known (no payment window could be established): money may
      // have been taken, and the pending blob is the only record of what the
      // payment was for. Every payment flow overwrites paymentAction and its
      // pending blob before redirecting, so a preserved stale blob cannot
      // misfire a later follow-up.
      if (!opts.preservePendingState) {
        this.clearPendingState();
      }
      this.router.navigate([`${base}/payment-information`], { queryParams: { payment: 'failed' } });
      return;
    }

    // Success path. The HeyCentric proxy webhook is responsible for the
    // payPurchase record only — each follow-up below applies its own
    // booking updates (links, price, duration) client-side.
    try {
      if (action === 'add_ons') {
        await this.handleAddOnsFollowUp();
        this.clearPendingState();
        this.router.navigate([`${base}/add-ons/confirmation`]);
        return;
      }
      if (action === 'reschedule') {
        await this.handleRescheduleFollowUp();
        this.clearPendingState();
        this.router.navigate([`${base}/reschedule/confirmation`]);
        return;
      }
      if (action === 'extend') {
        await this.handleExtendFollowUp();
        this.clearPendingState();
        this.router.navigate([`${base}/extend/confirmation`]);
        return;
      }
      if (action === 'balance') {
        await this.handleBalanceFollowUp();
        this.clearPendingState();
        this.router.navigate([`${base}/payment-information`], { queryParams: { payment: 'success' } });
        return;
      }
    } catch (err) {
      console.error('Post-payment follow-up failed', err);
      // Fall through to the default success page so the user still sees a
      // success state — the payment itself did succeed.
    }

    this.clearPendingState();
    this.router.navigate([`${base}/payment-information`], { queryParams: { payment: 'success' } });
  }

  /**
   * Balance-payment settlement: stamp each charged link's data.paid to its
   * full line total so the next payment-information visit shows it settled.
   * The list of links was persisted to localStorage before the gateway
   * redirect (payBalance / payment-amount). On a legacy pre-RWRSPI-1026
   * booking the £100 deposit line rides along, backfilling its stamp.
   */
  private async handleBalanceFollowUp(): Promise<void> {
    const paidLinksStr = localStorage.getItem('paidLinks');
    if (!paidLinksStr) return;
    let paidLinks: any[] = [];
    try {
      paidLinks = JSON.parse(paidLinksStr);
    } catch (err) {
      console.error('Failed to parse paidLinks', err);
      return;
    }
    for (const link of paidLinks) {
      if (!link?.item_id) continue;
      try {
        await this.apiService.updateServiceItemExtraLink(
          this.company.id,
          link.item_id,
          this.booking.id,
          {
            global_service_id: link.global_service_id ?? this.service.id,
            item_id: link.item_id,
            booking_id: `${this.booking.id}`,
            // Floored at the prior paid: settling a balance must never reduce a
            // line's recorded payment, which would discard credit carried from an
            // earlier downward reschedule.
            data: {
              ...link.data,
              paid: Math.max(lineTotal(link.data), Number(link.data?.paid) || 0),
            },
          },
        );
      } catch (err) {
        console.error(`Failed to stamp paid on link ${link.item_id} after balance payment`, err);
      }
    }
  }

  private async handleAddOnsFollowUp(): Promise<void> {
    const pendingStr = localStorage.getItem('pendingAddOnLinks');
    if (!pendingStr) return;
    let links: Array<{ item_id: string; data: any }> = [];
    try {
      links = JSON.parse(pendingStr);
    } catch (err) {
      console.error('Failed to parse pendingAddOnLinks', err);
      return;
    }
    for (const link of links) {
      if (!link?.item_id) continue;
      try {
        await this.apiService.linkExtraItem(
          this.company.id,
          this.service.id,
          this.booking.id,
          link.item_id,
          // Add-ons are paid in full at purchase — stamp the line settled so
          // it doesn't show as outstanding on payment-information.
          { ...link.data, paid: lineTotal(link.data) },
        );
      } catch (err) {
        console.error(`Failed to create add-on link for item ${link.item_id}`, err);
      }
    }

    // Bump the booking's price by the add-ons total (link costs are per-unit
    // in pounds; booking.price is pence) and stamp the booking questions for
    // the add-ons bought (confetti / first toast) in the same update.
    // Affirmative answers only — unselected add-ons send nothing.
    const totalPence = Math.round(
      links.reduce(
        (sum, l) => sum + Number(l?.data?.cost ?? 0) * Number(l?.data?.quantity ?? 0),
        0,
      ) * 100,
    );
    const body: any = {};
    if (totalPence > 0) {
      body.price = Number(this.booking.price) + totalPence;
    }
    const questionsStr = localStorage.getItem('pendingAddOnQuestions');
    if (questionsStr) {
      try {
        const questions = JSON.parse(questionsStr);
        if (Array.isArray(questions) && questions.length > 0) {
          body.questions = questions;
        }
      } catch (err) {
        console.error('Failed to parse pendingAddOnQuestions', err);
      }
    }
    if (Object.keys(body).length > 0) {
      try {
        const result: any = await this.apiService.updateBooking(
          body,
          this.booking.company_id,
          this.booking.id,
          false,
        );
        if (result && typeof result !== 'string') {
          this.dataStore.booking = result;
          localStorage.setItem('ceremony', JSON.stringify(result));
        }
      } catch (err) {
        console.error('Failed to update booking price/questions after add-ons purchase', err);
      }
    }

    // Email the customer the add-ons they bought.
    try {
      await this.extendState.sendAddOnEmail(this.booking, this.member?.email, links);
    } catch (addOnErr) {
      console.error('Failed to send add-on email', addOnErr);
    }

    // RWRSPI-1055: alert the office. Scoped to the purchase so a later,
    // separate add-on purchase still alerts.
    await this.alerts.send('addon', this.booking, this.purchaseId ?? this.bookingId);
  }

  private async handleRescheduleFollowUp(): Promise<void> {
    const pendingStr = localStorage.getItem(RescheduleService.PENDING_KEY);
    if (!pendingStr) return;
    let pending: any;
    try {
      pending = JSON.parse(pendingStr);
    } catch (err) {
      console.error('Failed to parse pendingReschedule', err);
      return;
    }
    await this.rescheduleService.performUpdates({
      booking: this.dataStore.booking ?? this.booking,
      options: pending.options ?? [],
      rescheduleService: pending.rescheduleService,
      slot: pending.slot,
      priceComparison: pending.priceComparison ?? [],
      priceDifference: pending.priceDifference ?? 0,
      bookedGlobalServiceId: pending.bookedGlobalServiceId,
      additionalFee: pending.additionalFee ?? null,
      paymentTaken: pending.paymentTaken ?? true,
      settleBalance: pending.settleBalance ?? true,
      newChargesDeferred: pending.newChargesDeferred ?? false,
    });
  }

  /**
   * Extend flow follow-up: link each chargeable extra-item created on the
   * summary page, then bump the booking's duration + price to reflect the new
   * extended ceremony. Tolerates per-link failures so a single bad link
   * doesn't block the duration/price update — the gateway has already taken
   * payment, so the user must still land on /extend/confirmation.
   */
  private async handleExtendFollowUp(): Promise<void> {
    const pendingStr = localStorage.getItem('pendingExtendLinks');
    if (!pendingStr) return;
    let pending: {
      links: Array<{ item_id: string; data: any }>;
      extraDurationSeconds: number;
      priceDeltaPence: number;
      roomHireUpdate?: { item_id: string; global_service_id: any; data: any } | null;
      questions?: Array<{ id: number; answer: boolean }>;
      settleFullBalance?: boolean;
    };
    try {
      pending = JSON.parse(pendingStr);
    } catch (err) {
      console.error('Failed to parse pendingExtendLinks', err);
      return;
    }
    // Blobs written before the payment choice existed were always full-settle.
    const settleFullBalance = pending.settleFullBalance ?? true;

    for (const link of pending.links ?? []) {
      if (!link?.item_id) continue;
      try {
        await this.apiService.linkExtraItem(
          this.company.id,
          this.service.id,
          this.booking.id,
          link.item_id,
          // Every chargeable pending link was paid in full at the gateway
          // (the £0 extension record line settles at 0 = its line total).
          { ...link.data, paid: lineTotal(link.data) },
        );
      } catch (err) {
        console.error(`Failed to create extend link for item ${link.item_id}`, err);
      }
    }

    // The extension charge is applied as an update to the existing room-hire
    // link's cost (PUT), not a new link — the room is the same, it just costs
    // more now it's hosting a longer ceremony.
    const roomHireUpdate = pending.roomHireUpdate;
    if (roomHireUpdate?.item_id) {
      try {
        await this.apiService.updateServiceItemExtraLink(
          this.company.id,
          roomHireUpdate.item_id,
          this.booking.id,
          {
            global_service_id: roomHireUpdate.global_service_id,
            item_id: roomHireUpdate.item_id,
            booking_id: `${this.booking.id}`,
            data: roomHireUpdate.data,
          },
        );
      } catch (err) {
        console.error(`Failed to update room-hire link ${roomHireUpdate.item_id} after extend`, err);
      }
    }

    // When the customer chose to settle the whole booking, every pre-existing
    // link's remainder was part of the charge — stamp them fully paid. The
    // room-hire link was already updated above, and the pending add-on links
    // were created settled. On the new-items-only choice the balance stays
    // outstanding, so the pre-existing links are left untouched.
    if (settleFullBalance) {
      try {
        const links = await this.apiService.getServiceItemExtraLinksForBooking(
          this.company.id,
          this.booking.id,
        ) as any;
        for (const link of Array.isArray(links) ? links : []) {
          if (roomHireUpdate?.item_id && String(link.item_id) === String(roomHireUpdate.item_id)) continue;
          if (lineOutstanding(link.data) <= 0) continue;
          try {
            await this.apiService.updateServiceItemExtraLink(
              this.company.id,
              link.item_id,
              this.booking.id,
              {
                global_service_id: link.global_service_id ?? this.service.id,
                item_id: link.item_id,
                booking_id: `${this.booking.id}`,
                data: { ...link.data, paid: lineTotal(link.data) },
              },
            );
          } catch (err) {
            console.error(`Failed to settle link ${link.item_id} after extend`, err);
          }
        }
      } catch (err) {
        console.error('Failed to fetch links for post-extend settlement', err);
      }
    }

    try {
      // Durations are inconsistent across endpoints: the single-booking GET
      // (this.booking) returns minutes, while the list endpoint returns
      // seconds (RWRSPI-968). Normalise the current duration to minutes, then
      // add the selected extension so we always send current-duration +
      // extension (e.g. 60 + 30 = 90), never a mixed seconds/minutes value.
      const extraMinutes = Math.round(Number(pending.extraDurationSeconds ?? 0) / 60);
      const newDurationMinutes = this.toMinutes(Number(this.booking.duration)) + extraMinutes;
      const newPrice = Number(this.booking.price) + Number(pending.priceDeltaPence ?? 0);
      const body: any = {
        id: this.booking.id,
        price: newPrice,
        duration: newDurationMinutes, // API expects minutes
        service_id: this.booking.service_id,
        resource_id: this.booking._links?.resource?.href?.split('/').pop(),
        datetime: this.booking.datetime,
        // Suppress the platform's default amend emails — we send our own
        // extended_ceremony_confirmation email below instead (RWRSPI-1002).
        notify: false,
        notify_admin: false,
        settings: {
          ...(this.booking.settings || {}),
          // 'extended' is reserved for the Dynamics integration — use the
          // supported 'rescheduled' status instead (RWRSPI-969). extended_at
          // still marks the booking as extended so it can't be extended twice.
          status: 'rescheduled',
          extended_at: new Date().toISOString(),
        },
      };
      // Stamp the add-on booking questions (confetti / first toast) in the
      // same update. Affirmative answers only.
      if (pending.questions?.length) {
        body.questions = pending.questions;
      }
      const result: any = await this.apiService.updateBooking(
        body,
        this.booking.company_id,
        this.booking.id,
        false,
      );
      if (result && typeof result !== 'string') {
        this.dataStore.booking = result;
        localStorage.setItem('ceremony', JSON.stringify(result));
      }

      // Send the customer the extended ceremony confirmation email (RWRSPI-1002).
      try {
        await this.apiService.sendExtendedCeremonyConfirmationEmail(
          this.booking.company_id,
          this.member?.email,
          'registeroffice@westminster.gov.uk',
          this.booking.id,
        );
      } catch (emailErr) {
        console.error('Failed to send extended ceremony confirmation email', emailErr);
      }

      // Email the customer the add-ons they bought (skips if none).
      try {
        await this.extendState.sendAddOnEmail(this.booking, this.member?.email, pending.links ?? []);
      } catch (addOnErr) {
        console.error('Failed to send add-on email', addOnErr);
      }

      // RWRSPI-1056: alert the office, but only if the duration/price update
      // above actually landed — updateBooking returns its error as a string.
      if (result && typeof result !== 'string') {
        await this.alerts.send('extended', this.booking, this.purchaseId ?? this.bookingId);
      }
    } catch (err) {
      console.error('Failed to update booking duration/price after extend', err);
    }
  }

  /**
   * Booking durations come back in different units depending on the endpoint:
   * the single-booking GET returns minutes, the list endpoint returns seconds
   * (RWRSPI-968). A ceremony is 30–90 minutes, so any value above 300 must be
   * seconds and is converted; smaller values are already minutes.
   */
  private toMinutes(duration: number): number {
    return CommonFunctionsService.bookingDurationToMinutes(duration);
  }

  private clearPendingState(): void {
    localStorage.removeItem('paymentAction');
    localStorage.removeItem('pendingAddOnLinks');
    localStorage.removeItem('pendingAddOnQuestions');
    localStorage.removeItem('pendingExtendLinks');
    localStorage.removeItem('paidLinks');
    localStorage.removeItem(RescheduleService.PENDING_KEY);
  }
}